import { ScreenContent } from "@shared/application-components"
import { formatPercentage } from "@shared/utils/formatters/formatPercentage"
import { Flex, Grid, GridCell, Link, Separator } from "@shared/primitive-components"
import { localDateTimeToString } from "@shared/utils/formatters/dates"
import moneyToString from "@shared/utils/formatters/moneyToString"
import { useCallback } from "react"
import { CONNECTIVITY_TARGET } from "../../../../../const/connectivity"
import * as DetailList from "../../../../components/DetailList"
import { Details } from "../../../../components/Details"
import { FleetSummary } from "../../../../components/FleetSummary"
import { PricingDetails } from "../../../../components/PricingDetails"
import { Policy } from "../../../api/useFindPolicy"
import * as styles from "./styles/generalStyles"

type Props = {
  policy: Policy
}

export const PolicyView = ({ policy }: Props) => {
  const navigateToDocumentsPortal = useCallback(() => {
    window.open(`https://portal.flockcover.com/policies/${policy.id}/documents`, `_blank`)
  }, [policy.id])

  const { connectivity } = policy
  const connectivityRate = connectivity?.connectivityRate
  const hasConnectivityRate = typeof connectivityRate === `number`

  const onCoverTotalLabel = connectivity ? connectivity.onCoverTotal : `-`
  const connectivityPercentageLabel = hasConnectivityRate ? formatPercentage(connectivityRate) : `-`
  const connectivityColor = hasConnectivityRate
    ? connectivityRate >= CONNECTIVITY_TARGET
      ? `green`
      : `red`
    : undefined

  return (
    <ScreenContent title={`Policy for ${policy.holder}`}>
      <Grid css={styles.fullWidth}>
        <GridCell phone={12} desktop={6}>
          <Details title="Policy details" summary={[`Here are the details of your customer's policy.`]}>
            <DetailList.Root>
              <DetailList.Item title="Policy number">{policy.number}</DetailList.Item>
              <DetailList.Item title="Product">Motor flat-fee</DetailList.Item>
              <DetailList.Item title="Policy inception date">
                {localDateTimeToString(policy.startTime, `en`, `dd/MM/yyyy`)}
              </DetailList.Item>
              <DetailList.Item title="Policy expiry date">
                {localDateTimeToString(policy.endTime, `en`, `dd/MM/yyyy`)}
              </DetailList.Item>
              <DetailList.Item title="Sector">{policy.sector}</DetailList.Item>
              <DetailList.Item title="Underwriter">
                <Link href={policy.underwriter.link} target="_blank" rel="noopener noreferrer">
                  {policy.underwriter.name}
                </Link>
              </DetailList.Item>
              <DetailList.Item title="Excess">{moneyToString(policy.excess)}</DetailList.Item>
              <DetailList.Item color={connectivityColor} title="Connectivity">
                {connectivityPercentageLabel}
              </DetailList.Item>
              <DetailList.Item title="Current Vehicles on Cover">{onCoverTotalLabel}</DetailList.Item>
            </DetailList.Root>
          </Details>
          <FleetSummary
            holder={policy.holder}
            premiumBands={policy.premiumBands}
            text="Here is the breakdown of the on cover vehicles on your customer's fleet."
            policyId={policy.id}
            policyNumber={policy.number}
            showAddButton
          />
        </GridCell>
        <GridCell phone={12} desktop={6}>
          <PricingDetails premium={policy.pricing.totalPremium}>
            <DetailList.Root>
              <DetailList.Item title="Premium">{moneyToString(policy.pricing.premium)}</DetailList.Item>
              <Flex flexDirection="column">
                {policy.pricing.brokerages.map((brokerage) => (
                  <DetailList.Item title={`Brokerage: ${brokerage.distributor}`} key={brokerage.distributor}>
                    {moneyToString(brokerage.brokerage)}
                  </DetailList.Item>
                ))}
              </Flex>
              <DetailList.Item title="Tax">{moneyToString(policy.pricing.tax)}</DetailList.Item>
              <DetailList.Item title="Fees">{moneyToString(policy.pricing.fees)}</DetailList.Item>
              <Separator size="hairlineFull" color="gray" />
              <DetailList.Item title="Total premium" bold>
                {moneyToString(policy.pricing.totalPremium)}
              </DetailList.Item>
            </DetailList.Root>
          </PricingDetails>
          <Details title="Documents" summary={[`Here are the documents for your customer's policy.`]}>
            <DetailList.Root>
              <DetailList.Item title="Welcome Pack">
                <Link onClick={navigateToDocumentsPortal} href="#">
                  View documents
                </Link>
              </DetailList.Item>
              <DetailList.Item title="Certificate of Insurance">
                <Link onClick={navigateToDocumentsPortal} href="#">
                  View documents
                </Link>
              </DetailList.Item>
              <DetailList.Item title="Policy Schedule">
                <Link onClick={navigateToDocumentsPortal} href="#">
                  View documents
                </Link>
              </DetailList.Item>
            </DetailList.Root>
          </Details>
        </GridCell>
      </Grid>
    </ScreenContent>
  )
}
